import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Form,
  Modal,
  message,
  Space,
  Popconfirm,
  Select,
  InputNumber,
  Switch,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getAllProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getAllCategories,
  getVendors,
} from "../../api/accounting";

const ProductTab = ({ selectedBrand = null }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render counter
  
  // Track current fetch to prevent race conditions
  const fetchIdRef = React.useRef(0);

  // Fetch products, categories, and vendors on component mount or when selectedBrand ID changes
  // Using selectedBrand?.id to prevent unnecessary refetches when object reference changes
  const selectedBrandId = selectedBrand?.id;
  useEffect(() => {
    if (selectedBrand) {
      fetchProducts();
      fetchCategories();
      fetchVendors();
    } else {
      setProducts([]);
      setCategories([]);
      setVendors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId]);

  // Fetch vendors when modal opens if they haven't been fetched yet
  useEffect(() => {
    if (isModalVisible && selectedBrand && vendors.length === 0) {
      fetchVendors();
    }
  }, [isModalVisible, selectedBrand]);

  const fetchProducts = async (filters = {}, useCurrentFilters = false) => {
    // Increment fetch ID to track this request
    const currentFetchId = ++fetchIdRef.current;
    
    setLoading(true);
    try {
      // CRITICAL: Only fetch products for the selected brand
      if (!selectedBrand) {
        setProducts([]);
        setLoading(false);
        return;
      }

      // Always filter by selected brand
      filters.brand = selectedBrand.id;
      
      // If useCurrentFilters is true, apply current search state
      if (useCurrentFilters && searchText) {
        filters.name = searchText;
      }

      const response = await getAllProducts(filters);
      
      // Only update state if this is still the latest request
      if (currentFetchId !== fetchIdRef.current) {
        return; // A newer request has been made, ignore this result
      }
      
      setProducts(Array.isArray(response.data) ? [...response.data] : []);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) return;
      console.error("Error fetching products:", error);
      message.error(
        "Failed to fetch products: " +
          (error.response?.data?.detail || error.message)
      );
      setProducts([]);
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    const filters = {};
    if (searchText) filters.name = searchText;
    fetchProducts(filters);
  };

  const resetSearch = () => {
    setSearchText("");
    fetchProducts();
  };

  const showAddModal = () => {
    setEditingProduct(null);
    form.resetFields();
    // Set default values
    form.setFieldsValue({
      product_type: "product",
      purchase_price: 0,
      sale_price: 0,
      tax_rate: 0,
      active: true,
    });
    // Ensure vendors are fetched when modal opens
    if (selectedBrand && vendors.length === 0) {
      fetchVendors();
    }
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingProduct(record);

    // Validate vendor exists in current brand's vendor list
    let validVendor = record.vendor;
    if (record.vendor && vendors.length > 0) {
      const vendorExists = vendors.some((v) => v.id === record.vendor);
      if (!vendorExists) {
        // Vendor doesn't exist in current brand, clear it
        validVendor = undefined;
        console.warn(
          `Vendor ${record.vendor} not found in current brand's vendor list, clearing vendor field`
        );
      }
    }

    form.setFieldsValue({
      name: record.name,
      description: record.description,
      product_type: record.product_type || "product",
      category: record.category,
      vendor: validVendor,
      purchase_price: record.purchase_price || 0,
      sale_price: record.sale_price || 0,
      tax_rate: record.tax_rate || 0,
      active: record.active !== undefined ? record.active : true,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const fetchCategories = async () => {
    try {
      const filters = selectedBrand ? { brand: selectedBrand.id } : {};
      const response = await getAllCategories(filters);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error("Failed to fetch categories");
    }
  };

  const fetchVendors = async () => {
    try {
      // CRITICAL: Only fetch vendors for the selected brand
      if (!selectedBrand) {
        console.log("ProductTab: No brand selected, cannot fetch vendors");
        setVendors([]);
        return;
      }

      const filters = { brand: selectedBrand.id };
      const response = await getVendors(filters);

      // Handle both direct array response and wrapped response
      let vendorsData = response.data;
      if (Array.isArray(vendorsData)) {
        setVendors(vendorsData);
      } else if (vendorsData && Array.isArray(vendorsData.results)) {
        setVendors(vendorsData.results);
      } else {
        setVendors([]);
      }
    } catch (error) {
      console.error("ProductTab: Error fetching vendors:", error);
      console.error("ProductTab: Error response:", error.response);
      console.error(
        "ProductTab: Error details:",
        error.response?.data || error.message
      );
      const errorMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Unknown error";
      message.error("Failed to fetch vendors: " + errorMsg);
      setVendors([]);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Add brand if selected
      if (selectedBrand) {
        values.brand = selectedBrand.id;
      } else {
        message.error("Please select a brand first");
        return;
      }

      // Ensure all required fields have values
      if (!values.product_type) values.product_type = "product";
      if (values.purchase_price === undefined) values.purchase_price = 0;
      if (values.sale_price === undefined) values.sale_price = 0;
      if (values.tax_rate === undefined) values.tax_rate = 0;
      if (values.active === undefined) values.active = true;

      if (editingProduct) {
        const response = await updateProduct(editingProduct.id, values);
        // Handle both axios wrapped response and direct data response
        const updatedProduct = response?.data?.id ? response.data : (response?.id ? response : { ...editingProduct, ...values });
        message.success("Product updated successfully");
        
        // Immediately update the item in state (optimistic update)
        setProducts(prevProducts => {
          const newProducts = prevProducts.map(p => 
            p.id === editingProduct.id ? { ...p, ...values, ...updatedProduct } : p
          );
          return [...newProducts]; // Force new array reference
        });
      } else {
        // CREATE NEW PRODUCT
        await createProduct(values);
        
        message.success("Product created successfully");
        
        // Clear search to show all products
        setSearchText("");
        
        // Refetch products to get the newly created one with server-generated fields
        await fetchProducts();
        
        // Force table re-render
        setRefreshKey(prev => prev + 1);
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('productsUpdated'));
        
        setIsModalVisible(false);
        return; // Exit early for create path
      }

      // For UPDATE path
      setIsModalVisible(false);
      setRefreshKey(prev => prev + 1);
      setSearchText("");
      window.dispatchEvent(new CustomEvent('productsUpdated'));
    } catch (error) {
      // Check if this is a form validation error (Ant Design form validation)
      if (error.errorFields && Array.isArray(error.errorFields)) {
        // This is a form validation error, not an API error
        const fieldErrors = error.errorFields
          .map((field) => {
            return `${field.name.join(".")}: ${field.errors.join(", ")}`;
          })
          .join("; ");
        message.error("Please fix the form errors: " + fieldErrors);
        return;
      }

      // This is an API error
      console.error("Error saving product:", error);
      const errorMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Unknown error";
      message.error("Failed to save product: " + errorMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteProduct(id);
      message.success("Product deleted successfully");
      
      // Immediately remove the deleted item from state (optimistic update)
      setProducts(prevProducts => prevProducts.filter(p => p.id !== id));
      
      // Force table re-render
      setRefreshKey(prev => prev + 1);
      
      // Dispatch event to notify other components to refresh products
      window.dispatchEvent(new CustomEvent('productsUpdated'));
    } catch (error) {
      console.error("Error deleting product:", error);
      message.error("Failed to delete product");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      render: (vendorId) => {
        const vendor = vendors.find((v) => v.id === vendorId);
        return vendor ? vendor.name : "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            type="primary"
            size="small"
          />
          <Popconfirm
            title="Are you sure you want to delete this product?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<DeleteOutlined />}
              type="primary"
              danger
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Products Management" variant="outlined">
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="Search by name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            onPressEnter={handleSearch}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Search
          </Button>
          <Button onClick={resetSearch}>Reset</Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddModal}
            style={{ marginLeft: "auto" }}
          >
            Add Product
          </Button>
        </Space>
      </div>

      <Table
        key={refreshKey}
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingProduct ? "Edit Product" : "Add New Product"}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleSubmit}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: "Please enter product name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="product_type"
            label="Product Type"
            rules={[{ required: true, message: "Please select product type" }]}
          >
            <Select>
              <Select.Option value="product">Product</Select.Option>
              <Select.Option value="service">Service</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="vendor"
            label="Vendor"
            rules={[{ required: true, message: "Please select a vendor" }]}
          >
            <Select
              placeholder="Select a vendor"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              notFoundContent={
                vendors.length === 0
                  ? "No vendors found. Please ensure a brand is selected and vendors exist for this brand."
                  : "No vendors match your search"
              }
              loading={loading}
            >
              {vendors.map((vendor) => (
                <Select.Option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProductTab;
